/**
 * Ciudad de residencia y sueldo deseado del formulario publico (B2).
 *
 * Viven fuera del componente por dos razones: exportar funciones desde un
 * archivo de componente rompe el fast refresh y ese aviso cuenta contra el
 * tope del lint, y asi las reglas se pueden probar sin montar el formulario
 * de 800 lineas.
 */

/** Lo que aguanta la columna `residence_city`. */
export const MAX_CIUDAD = 120

/** Lo que aguanta `decimal(10,2)`. */
export const MAX_SUELDO = 99999999.99

/**
 * El valor del input de sueldo, listo para el estado del formulario.
 *
 * Vacio es `undefined`, no 0: un cero guardado por dejar el campo en blanco se
 * leeria despues como una expectativa real. Y se usa Number y no parseInt
 * porque parseInt se comeria los centavos.
 */
export const sueldoDesdeInput = (valor: string): number | undefined =>
  valor === '' ? undefined : Number(valor)

export const errorDeCiudad = (valor: string): string | undefined => {
  const limpia = valor.trim()
  if (!limpia) return 'La ciudad de residencia es requerida'
  if (limpia.length > MAX_CIUDAD) return `La ciudad no puede exceder ${MAX_CIUDAD} caracteres`
  return undefined
}

export const errorDeSueldoDeseado = (valor?: number): string | undefined => {
  if (valor === undefined) return undefined
  if (Number.isNaN(valor)) return 'El sueldo deseado debe ser un número'
  if (valor < 0) return 'El sueldo deseado no puede ser negativo'
  if (valor > MAX_SUELDO) return 'El sueldo deseado es demasiado alto'

  // decimal(10,2) truncaria el tercer decimal en silencio. La tolerancia es
  // por el binario: 0.07 * 100 da 7.000000000000001, no 7.
  const centavos = valor * 100
  if (Math.abs(centavos - Math.round(centavos)) > 1e-6) {
    return 'El sueldo deseado admite máximo dos decimales'
  }

  return undefined
}

type ValidezDelSueldo = Pick<
  ValidityState,
  'stepMismatch' | 'rangeUnderflow' | 'rangeOverflow' | 'badInput'
>

/**
 * El aviso del navegador para el input de sueldo, en espanol.
 *
 * Con step=0.01 Chrome ataja el tercer decimal por su cuenta, antes de que
 * corra errorDeSueldoDeseado, y su mensaje sale en el idioma del navegador:
 * en medio de un formulario en espanol aparecia un «Please enter a valid
 * value». El dato malo no pasaba de todos modos; esto es solo el texto.
 *
 * Cadena vacia significa que no hay nada que reportar, que es justo lo que
 * setCustomValidity necesita para dar por bueno al campo.
 */
export const mensajeNativoDelSueldo = (validez: ValidezDelSueldo): string => {
  if (validez.stepMismatch) return 'El sueldo deseado admite máximo dos decimales'
  if (validez.rangeUnderflow) return 'El sueldo deseado no puede ser negativo'
  if (validez.rangeOverflow) return 'El sueldo deseado es demasiado alto'
  if (validez.badInput) return 'Escribe el sueldo deseado como número'
  return ''
}
